import { useEffect, useRef, useState } from "react"

/**
 * Debounced Auto-Save fuer Email-Drafts.
 *
 * - Ruft `save(values)` an, sobald sich `values` geaendert haben (1.5s nach
 *   letztem Tastendruck) — keine Spam-Requests waehrend des Tippens.
 * - Mirror in `localStorage` unter `key`, damit ein Page-Reload mit ungeflushtem
 *   Edit nichts verliert. Wird nach erfolgreichem Save geleert.
 * - Status: idle → dirty → saving → saved | error
 *
 * **Wichtig:** `values` muss `JSON.stringify`-aequivalent sein (kein Date,
 * keine Funktionen). `save` muss stable sein (useCallback ausserhalb).
 */
export type AutoSaveStatus = "idle" | "dirty" | "saving" | "saved" | "error"

export interface UseAutoSaveDraftOptions<T extends Record<string, unknown>> {
  values: T
  save: (values: T) => Promise<void>
  /** localStorage-Key fuer Reload-Recovery. Empfehlung: `auto-draft:<emailId>:<form>`. */
  storageKey?: string
  /** Debounce in ms — Default 1500. */
  debounceMs?: number
  /** Wenn `false`, ist der Hook komplett deaktiviert (z.B. solange initial geladen wird). */
  enabled?: boolean
}

export function useAutoSaveDraft<T extends Record<string, unknown>>({
  values,
  save,
  storageKey,
  debounceMs = 1500,
  enabled = true,
}: UseAutoSaveDraftOptions<T>) {
  const [status, setStatus] = useState<AutoSaveStatus>("idle")
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Damit der erste Render nicht direkt einen Save triggert.
  const lastSerialized = useRef<string | null>(null)
  // Stabile Save-Ref, damit der Effekt nicht jeden Render neu feuert.
  const saveRef = useRef(save)
  saveRef.current = save
  // Stabile Values-Ref — der Unmount-Cleanup-Effekt unten hat ein leeres
  // Dependency-Array (laeuft nur beim echten Unmount). Ohne Ref haette er
  // einen Stale-Closure auf die `values` vom ersten Render und wuerde
  // bei Unmount potenziell den falschen Body persistieren.
  const valuesRef = useRef(values)
  valuesRef.current = values

  // Initial-Snapshot beim Aktivieren / values-Reset (z.B. neuer Email-Tab).
  useEffect(() => {
    if (!enabled) return
    lastSerialized.current = JSON.stringify(values)
    setStatus("idle")
    // Reset-Anker — values-Identitaet hat sich geaendert weil Caller das tat.
    // Hier: NICHT von `values` abhaengig (sonst Endlosschleife).
    // Wir nutzen storageKey als Reset-Trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, enabled])

  useEffect(() => {
    if (!enabled) return
    const serialized = JSON.stringify(values)
    if (lastSerialized.current === null) {
      // Initial-Snapshot setzen
      lastSerialized.current = serialized
      return
    }
    if (serialized === lastSerialized.current) return

    // Mirror sofort in localStorage — auch wenn noch debounced.
    // Wrappen in {payload, saved_at, key} damit Reads den Mirror gegen
    // den richtigen Storage-Key verifizieren koennen (Schutz gegen
    // verschmutzte Mirror-Eintraege bei Cross-Mail-Switches).
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          payload: values,
          saved_at: Date.now(),
          key: storageKey,
        }))
      } catch { /* quota/private mode */ }
    }
    setStatus("dirty")

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      setStatus("saving")
      try {
        await saveRef.current(values)
        lastSerialized.current = serialized
        setStatus("saved")
        setSavedAt(new Date())
        if (storageKey) {
          try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
        }
      } catch {
        setStatus("error")
      }
    }, debounceMs)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [values, debounceMs, enabled, storageKey])

  // Beim Unmount: ausstehenden Save sofort flushen — sonst gehen Edits verloren
  // wenn User den Tab wechselt. Nutzt valuesRef damit der Cleanup die
  // AKTUELLEN values sieht (sonst stale-closure mit values vom ersten Render).
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        const current = valuesRef.current
        const serialized = JSON.stringify(current)
        if (serialized !== lastSerialized.current) {
          // Best-effort: fire-and-forget. Im Worst-Case greift der localStorage-Mirror.
          saveRef.current(current).catch(() => { /* ignore */ })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { status, savedAt }
}

/**
 * Liest einen evtl. vorhandenen localStorage-Mirror eines unflushed Drafts.
 * Aufrufen beim Mount — falls `null`, einfach defaults nehmen.
 *
 * Schutz gegen verschmutzte Mirror-Eintraege:
 *   - Mirror muss zum aktuellen `storageKey` gehoeren (key-Property im Payload)
 *   - Mirror darf nicht aelter als `maxAgeMs` sein (Default 24h)
 *   - Wenn `serverSavedAt` gesetzt ist und neuer als der Mirror, ignorieren
 *     (Server-Wert hat Vorrang — z.B. wenn User auf anderem Tab gesaved hat)
 *   - Bei Verstoss: Mirror loeschen + null zurueckgeben
 *
 * Legacy-Format (raw payload ohne wrapper) wird transparent migriert: bei
 * fehlendem `key`/`saved_at` wird der Eintrag als sauber akzeptiert (ein
 * Reload reicht — Schutz greift ab dem naechsten Save).
 */
export function readDraftFromStorage<T>(
  storageKey: string,
  opts?: { serverSavedAt?: string | Date | null; maxAgeMs?: number },
): T | null {
  const maxAgeMs = opts?.maxAgeMs ?? 1000 * 60 * 60 * 24
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Wrapped format ({payload, saved_at, key})
    if (parsed && typeof parsed === "object" && "payload" in parsed) {
      const { payload, saved_at, key } = parsed as { payload: T; saved_at?: number; key?: string }
      if (key && key !== storageKey) {
        try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
        return null
      }
      if (saved_at && Date.now() - saved_at > maxAgeMs) {
        try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
        return null
      }
      if (saved_at && opts?.serverSavedAt) {
        const serverMs = new Date(opts.serverSavedAt).getTime()
        if (!Number.isNaN(serverMs) && serverMs > saved_at) {
          try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
          return null
        }
      }
      return payload as T
    }
    // Legacy raw format — direkt zurueckgeben (wird beim naechsten Save migriert).
    return parsed as T
  } catch {
    return null
  }
}
